// Receipts on Petty cash entries (idea 38). Raw bytes go up as the request
// body with the name in a header (same as ideaFiles.ts and depositReconFile.ts);
// the Worker stores them in the clinic's Money Drive folder and records a link.
// After each write the entry in the local store is updated so the paperclip
// count changes without a reload.
import { API_BASE } from './config';
import { authToken } from './auth';
import { withViewAs } from './viewAs';
import { authedWrite } from './apiClient';
import { upsertEntity, getEntityTable } from './store';

export const MAX_UPLOAD_BYTES = 25 * 1024 * 1024;

export interface PettyReceipt {
  id: number;
  pettyCashId?: number;
  url: string;
  name: string | null;
  addedBy?: string;
  addedAt?: string;
}

async function setReceipts(
  pettyCashId: number,
  change: (current: PettyReceipt[]) => PettyReceipt[],
): Promise<void> {
  const existing = getEntityTable('pettyCash')[String(pettyCashId)] as
    | { receipts?: PettyReceipt[] } | undefined;
  if (!existing) return;
  await upsertEntity('pettyCash', pettyCashId, {
    ...existing,
    receipts: change(existing.receipts ?? []),
  });
}

export async function uploadPettyReceipt(pettyCashId: number, file: File): Promise<PettyReceipt> {
  if (file.size === 0) throw new Error(`${file.name} is empty`);
  if (file.size > MAX_UPLOAD_BYTES) {
    throw new Error(
      `${file.name} is ${Math.round(file.size / 1048576)} MB — the limit is `
      + `${MAX_UPLOAD_BYTES / 1048576} MB. Put it in Drive and paste the link instead.`,
    );
  }
  const token = authToken.value;
  const res = await fetch(`${API_BASE}${withViewAs(`/api/v1/money/petty-cash/${pettyCashId}/receipts`)}`, {
    method: 'POST',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      'content-type': file.type || 'application/octet-stream',
      'x-file-name': encodeURIComponent(file.name),
      'idempotency-key': crypto.randomUUID(),
    },
    body: file,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error((body as { error?: string }).error || `upload failed (HTTP ${res.status})`);
  }
  const receipt = (body as { receipt: PettyReceipt }).receipt;
  await setReceipts(pettyCashId, (cur) => [...cur.filter((r) => r.id !== receipt.id), receipt]);
  return receipt;
}

export async function deletePettyReceipt(pettyCashId: number, receiptId: number): Promise<void> {
  await authedWrite('DELETE', `/api/v1/money/petty-cash-receipts/${receiptId}`);
  await setReceipts(pettyCashId, (cur) => cur.filter((r) => r.id !== receiptId));
}
